export type RunStatus = "correct" | "incorrect" | "crashed";

export interface Model {
  id: string;
  name: string;
  vendor: string;
}

export interface Problem {
  id: number;
  expected: boolean;
}

export interface CellResult {
  /** Per-run outcome (3 runs) */
  runs: RunStatus[];
  /** Per-run model output — null means the run crashed */
  outputs: (boolean | null)[];
  /** Aggregate: majority of non-crashed runs are correct (≥ half, and ≥1 ran) */
  correct: boolean;
  /** Aggregate: majority output of non-crashed runs */
  output: boolean;
}

export interface ModelData {
  id: string;
  name: string;
  vendor: string;
  results: Record<number, CellResult>;
}

export interface Dataset {
  problems: Problem[];
  models: ModelData[];
}

/* ── deterministic pseudo-random ─────────────────── */
function rng(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

/* ── generate 200 problems ───────────────────────── */
function makeProblems(): Problem[] {
  const problems: Problem[] = [];
  for (let i = 1; i <= 200; i++) {
    problems.push({ id: i, expected: rng(i * 7) > 0.45 });
  }
  return problems;
}

/* ── models with realistic accuracy profiles ─────── */
const MODEL_PROFILES: { id: string; name: string; vendor: string; accuracy: number }[] = [
  { id: "gpt-5",                name: "GPT-5",                  vendor: "OpenAI",      accuracy: 0.85 },
  { id: "gpt-4.1",              name: "GPT-4.1",                vendor: "OpenAI",      accuracy: 0.81 },
  { id: "o3",                   name: "o3",                     vendor: "OpenAI",      accuracy: 0.95 },
  { id: "o4-mini",              name: "o4-mini",                vendor: "OpenAI",      accuracy: 0.88 },
  { id: "gpt-4o",               name: "GPT-4o",                 vendor: "OpenAI",      accuracy: 0.79 },
  { id: "o1",                   name: "o1",                     vendor: "OpenAI",      accuracy: 0.86 },
  { id: "o1-mini",              name: "o1-mini",                vendor: "OpenAI",      accuracy: 0.76 },
  { id: "claude-3-7-sonnet",    name: "Claude 3.7 Sonnet",      vendor: "Anthropic",   accuracy: 0.92 },
  { id: "claude-3-5-sonnet",    name: "Claude 3.5 Sonnet",      vendor: "Anthropic",   accuracy: 0.84 },
  { id: "claude-3-opus",        name: "Claude 3 Opus",          vendor: "Anthropic",   accuracy: 0.80 },
  { id: "gemini-2-0-flash",     name: "Gemini 2.0 Flash",       vendor: "Google",      accuracy: 0.82 },
  { id: "gemini-1-5-pro",       name: "Gemini 1.5 Pro",         vendor: "Google",      accuracy: 0.79 },
  { id: "gemini-2-5-pro",       name: "Gemini 2.5 Pro",         vendor: "Google",      accuracy: 0.93 },
  { id: "deepseek-r1",          name: "DeepSeek-R1",            vendor: "DeepSeek",    accuracy: 0.94 },
  { id: "deepseek-v3",          name: "DeepSeek-V3",            vendor: "DeepSeek",    accuracy: 0.83 },
  { id: "deepseek-r1-distill",  name: "DeepSeek-R1-Distill",    vendor: "DeepSeek",    accuracy: 0.77 },
  { id: "llama-3-1-405b",       name: "Llama 3.1-405B",         vendor: "Meta",        accuracy: 0.78 },
  { id: "llama-3-3-70b",        name: "Llama 3.3-70B",          vendor: "Meta",        accuracy: 0.74 },
  { id: "qwen2-5-72b",          name: "Qwen2.5-72B",            vendor: "Alibaba",     accuracy: 0.75 },
  { id: "qwen2-5-max",          name: "Qwen2.5-Max",            vendor: "Alibaba",     accuracy: 0.82 },
  { id: "qwen3-235b",           name: "Qwen3-235B",             vendor: "Alibaba",     accuracy: 0.87 },
  { id: "phi-4",                name: "Phi-4",                  vendor: "Microsoft",   accuracy: 0.76 },
  { id: "grok-3",               name: "Grok-3",                 vendor: "xAI",         accuracy: 0.84 },
  { id: "grok-2",               name: "Grok-2",                 vendor: "xAI",         accuracy: 0.77 },
  { id: "step-2",               name: "Step-2",                 vendor: "StepFun",     accuracy: 0.74 },
];

function makeModels(problems: Problem[]): ModelData[] {
  return MODEL_PROFILES.map((profile, mi) => {
    const results: Record<number, CellResult> = {};
    problems.forEach((problem, pi) => {
      const runs: RunStatus[] = [];
      const outputs: (boolean | null)[] = [];

      // Cell-level base outcome (strongly correlated across runs)
      const baseR = rng(mi * 1000 + pi * 13 + 7);
      const cellCorrect = baseR < profile.accuracy;

      for (let run = 0; run < 3; run++) {
        // ~4% crash probability per run
        const crashR = rng(mi * 3000 + pi * 31 + run * 97 + 3);
        if (crashR < 0.04) {
          runs.push("crashed");
          outputs.push(null);
        } else {
          // 7% chance to deviate from the cell-level outcome → ~80% solid squares
          const flipR = rng(mi * 5000 + pi * 17 + run * 73 + 11);
          const runCorrect = flipR < 0.07 ? !cellCorrect : cellCorrect;
          runs.push(runCorrect ? "correct" : "incorrect");
          outputs.push(runCorrect ? problem.expected : !problem.expected);
        }
      }

      // Aggregate: majority of non-crashed runs are correct (≥ half, and ≥1 ran)
      const completedRuns = runs.filter(r => r !== "crashed");
      const correctCount  = completedRuns.filter(r => r === "correct").length;
      const correct = completedRuns.length > 0 && correctCount > completedRuns.length / 2;

      // Aggregate output: majority of non-crashed outputs
      const validOutputs = outputs.filter((o): o is boolean => o !== null);
      const trueCount = validOutputs.filter(Boolean).length;
      const output = validOutputs.length > 0 ? trueCount >= Math.ceil(validOutputs.length / 2) : false;

      results[problem.id] = { runs, outputs, correct, output };
    });
    return { id: profile.id, name: profile.name, vendor: profile.vendor, results };
  });
}

const PROBLEMS = makeProblems();
const MODELS   = makeModels(PROBLEMS);

export const MOCK_DATASET: Dataset = {
  problems: PROBLEMS,
  models: MODELS,
};